import difference from '@turf/difference';
import intersect from '@turf/intersect';
import union from '@turf/union';
import bbox from '@turf/bbox';
import {
  SCENARIO_DOMAINS,
  type ScenarioDomainKey,
} from 'lib/evaluation-case/scenario-domains';
import type { ScenarioFootprint } from './footprints';

type Area = GeoJSON.Feature<GeoJSON.Polygon | GeoJSON.MultiPolygon>;

/**
 * The case map's state, worked out from the form's picks, saved or not, as the spec's coverage
 * map has it (Evaluation Case Updates, Story 4, AC 6-11). It uses the Cases list tiles' state names
 * and their rule (Map::Tiles::EvaluationCases): green waits for all eight domains. Each coverage
 * domain (the shape-carrying five and Type Curve, whose footprint is the areas it prices) draws a
 * red footprint and the green is where all of them reach. Price Deck and DCA draw nothing and
 * shrink no green, but while one is missing the case is unconfirmed and no green renders anywhere.
 * - 'bare': no footprint is drawn; a bare basemap, no legend;
 * - 'incomplete': a domain has no scenario, or still holds the archived one it loaded with (as
 *   EvaluationCase::Readiness counts it): unconfirmed, the red stack alone;
 * - 'pending': every domain assigned, but a changed pick's footprint is still loading, so the
 *   previous picks' common region is not drawn;
 * - 'empty': every domain assigned, but the footprints share no area, or one pins no geometry
 *   (as EvaluationCase::CoverageGeometry stores NULL for both): all of it stays red;
 * - 'unavailable': a footprint was too large to send (overCap), assigned or not, or every domain is
 *   assigned but the intersection failed, so the common region can't be drawn here;
 * - 'complete': every domain assigned, with a common region: green, cut out of the red stack.
 */
export type CaseMapState =
  'bare' | 'incomplete' | 'pending' | 'empty' | 'unavailable' | 'complete';

/** `{ domain key => the archived scenario it loaded with }` (the tiles' Archived tag). */
export type ArchivedScenarioIds = Partial<Record<ScenarioDomainKey, number>>;

type Picks = Record<ScenarioDomainKey, { id: number } | null>;

/**
 * The domains still holding the archived scenario the case loaded with, in registry order.
 *
 * @param {object} scenarios - `{ domain key => choice | null }`.
 * @param {ArchivedScenarioIds} archivedScenarioIds - The archived scenario per domain.
 * @return {ScenarioDomainKey[]} The archived keys.
 */
export function archivedDomains(
  scenarios: Picks,
  archivedScenarioIds: ArchivedScenarioIds
): ScenarioDomainKey[] {
  return SCENARIO_DOMAINS.filter(
    (domain) =>
      scenarios[domain.key] !== null &&
      scenarios[domain.key]?.id === archivedScenarioIds[domain.key]
  ).map((domain) => domain.key);
}

/**
 * The footprints the map can draw, as features carrying their domain.
 *
 * @param {ScenarioFootprint[]} footprints - The endpoint's footprints.
 * @return {GeoJSON.FeatureCollection} One feature per drawable footprint.
 */
export function footprintFeatures(
  footprints: readonly ScenarioFootprint[]
): GeoJSON.FeatureCollection<GeoJSON.Polygon | GeoJSON.MultiPolygon> {
  return {
    type: 'FeatureCollection',
    features: footprints
      .filter((footprint) => footprint.polygon)
      .map((footprint) => ({
        type: 'Feature',
        properties: { domain: footprint.domain },
        geometry: footprint.polygon as GeoJSON.Polygon | GeoJSON.MultiPolygon,
      })),
  };
}

const boxesOverlap = (a: number[], b: number[]) =>
  a[0] <= b[2] && b[0] <= a[2] && a[1] <= b[3] && b[1] <= a[3];

/**
 * Whether one box lies within another, edges included.
 *
 * @param {number[]} inner - [minX, minY, maxX, maxY].
 * @param {number[]} outer - [minX, minY, maxX, maxY].
 * @return {boolean} Whether inner is within outer.
 */
const boxInside = (inner: number[], outer: number[]) =>
  outer[0] <= inner[0] &&
  outer[1] <= inner[1] &&
  inner[2] <= outer[2] &&
  inner[3] <= outer[3];

/**
 * The areas less any whose geometry repeats one before it. Several scenarios often pin the same
 * shapes, so the server sends byte-identical footprints. Clipped against an exact copy of a
 * footprint already in it, the running result's computed vertices lie a float's width off the
 * copy's edges, and polygon-clipping can't close a ring there ("Unable to complete output ring").
 * A copy adds nothing to an intersection or a union, so it is dropped before either.
 *
 * @param {Area[]} areas - The footprints.
 * @return {Area[]} The first of each distinct geometry, in order.
 */
const distinctAreas = (areas: readonly Area[]): Area[] => {
  const seen = new Set<string>();
  return areas.filter((area) => {
    const key = JSON.stringify(area.geometry.coordinates);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

type BoxedRing = { ring: GeoJSON.Position[]; box: number[] };

/**
 * A ring's box.
 *
 * @param {GeoJSON.Position[]} ring - A closed ring.
 * @return {number[]} [minX, minY, maxX, maxY].
 */
const ringBox = (ring: GeoJSON.Position[]) =>
  bbox({ type: 'LineString', coordinates: ring });

/**
 * Every ring of an area, outer rings and holes of every part, each with its box.
 *
 * @param {Area} area - A polygon or multipolygon feature.
 * @return {BoxedRing[]} The rings.
 */
const boxedRings = (area: Area): BoxedRing[] =>
  (area.geometry.type === 'Polygon'
    ? area.geometry.coordinates
    : area.geometry.coordinates.flat()
  ).map((ring) => ({ ring, box: ringBox(ring) }));

/**
 * Whether a point lies inside an area's rings, by even-odd ray casting across all of them: a
 * multipolygon's parts don't overlap and its holes sit inside their parts, so an odd number of
 * crossings is inside. Rings whose box misses the point are passed over.
 *
 * @param {number[]} point - [lng, lat].
 * @param {BoxedRing[]} rings - The area's rings (boxedRings).
 * @return {boolean} Whether the point is inside.
 */
const insideRings = ([x, y]: number[], rings: BoxedRing[]): boolean => {
  let inside = false;
  for (const { ring, box } of rings) {
    if (x < box[0] || x > box[2] || y < box[1] || y > box[3]) continue;
    for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
      const [xi, yi] = ring[i];
      const [xj, yj] = ring[j];
      if (yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi)
        inside = !inside;
    }
  }
  return inside;
};

/**
 * Decimal places the server writes footprints with (ScenarioFootprints::GEOJSON_DECIMALS): their
 * grid.
 */
const FOOTPRINT_DECIMALS = 9;

/**
 * Degrees a box is widened by before an edge is tested against it: a margin for rounding, so an
 * edge a float's width outside the box still counts as touching it.
 */
const BOX_SLACK = 1e-9;

/**
 * Whether the segment from a to b touches a box, edges and corners included (Liang-Barsky).
 *
 * @param {number[]} a - One end.
 * @param {number[]} b - The other end.
 * @param {number[]} box - [minX, minY, maxX, maxY].
 * @return {boolean} Whether any of the segment lies in the box.
 */
const segmentTouchesBox = (
  [ax, ay]: number[],
  [bx, by]: number[],
  box: number[]
): boolean => {
  if (
    Math.max(ax, bx) < box[0] ||
    Math.min(ax, bx) > box[2] ||
    Math.max(ay, by) < box[1] ||
    Math.min(ay, by) > box[3]
  )
    return false;
  const dx = bx - ax;
  const dy = by - ay;
  let enter = 0;
  let exit = 1;
  for (const [p, q] of [
    [-dx, ax - box[0]],
    [dx, box[2] - ax],
    [-dy, ay - box[1]],
    [dy, box[3] - ay],
  ]) {
    if (p === 0) {
      if (q < 0) return false;
    } else if (p < 0) {
      enter = Math.max(enter, q / p);
    } else {
      exit = Math.min(exit, q / p);
    }
    if (enter > exit) return false;
  }
  return true;
};

/**
 * A box widened by BOX_SLACK on every side.
 *
 * @param {number[]} box - [minX, minY, maxX, maxY].
 * @return {number[]} The widened box.
 */
const padded = ([minX, minY, maxX, maxY]: number[]) => [
  minX - BOX_SLACK,
  minY - BOX_SLACK,
  maxX + BOX_SLACK,
  maxY + BOX_SLACK,
];

/**
 * Whether `outer` covers all of `inner`, exact in the safe direction. For each part of inner, if
 * no edge of outer touches the part's box (widened by BOX_SLACK), outer is wholly in or wholly out
 * over that box, so one vertex of the part settles it, holes and all. Where an edge does touch the
 * box, it answers no and the clip runs as it would have: a no is never wrong, only slower. Outer's
 * edges are first narrowed to those touching inner's whole box; with none, one vertex settles all
 * of inner. turf 6's booleanContains takes no multipolygons, which every footprint here can be.
 *
 * @param {Area} outer - A footprint.
 * @param {Area} inner - The region so far.
 * @return {boolean} Whether outer covers inner.
 */
const covers = (outer: Area, inner: Area): boolean => {
  const innerBox = bbox(inner);
  if (!boxInside(innerBox, bbox(outer))) return false;
  const outerRings = boxedRings(outer);
  const around = padded(innerBox);
  const near = outerRings
    .filter(({ box }) => boxesOverlap(box, around))
    .flatMap(({ ring }) =>
      ring
        .slice(1)
        .map((point, i) => [ring[i], point])
        .filter(([a, b]) => segmentTouchesBox(a, b, around))
    );
  const parts =
    inner.geometry.type === 'Polygon'
      ? [inner.geometry.coordinates]
      : inner.geometry.coordinates;
  if (near.length === 0) return insideRings(parts[0][0][0], outerRings);
  return parts.every(([shell]) => {
    const box = padded(ringBox(shell));
    return (
      !near.some(([a, b]) => segmentTouchesBox(a, b, box)) &&
      insideRings(shell[0], outerRings)
    );
  });
};

/**
 * Where every footprint reaches: their intersection, folded pairwise. Boxes are checked first,
 * so footprints that can't overlap skip the polygon clip. Repeated footprints are dropped
 * (distinctAreas), and one that covers the region so far, such as a nationwide Taxes scenario, is
 * skipped (covers): neither clip could change the region, and either can make polygon-clipping
 * throw. After each clip the region is snapped back onto the footprints' grid (snapArea), so its
 * computed vertices land on the edges it shares with footprints still to come: a near copy that
 * isn't byte-identical (another start vertex, an extra vertex, a superset) then clips cleanly too.
 *
 * @param {Area[]} areas - The footprints, at least one.
 * @return {object} `region` (null when they share no area), or `failed` when a clip threw.
 */
export function commonRegion(areas: readonly Area[]): {
  region: Area | null;
  failed: boolean;
} {
  if (areas.length === 0) return { region: null, failed: false };

  const boxes = areas.map((area) => bbox(area));
  if (!boxes.every((box) => boxes.every((other) => boxesOverlap(box, other))))
    return { region: null, failed: false };

  try {
    const [first, ...rest] = distinctAreas(areas);
    let region: Area | null = first;
    for (const area of rest) {
      if (covers(area, region)) continue;
      const clipped = intersect(region, area) as Area | null;
      region = clipped && snapArea(clipped, FOOTPRINT_DECIMALS);
      if (!region) break;
    }
    return { region, failed: false };
  } catch {
    return { region: null, failed: true };
  }
}

/** Everywhere a scenario reaches, and whether the union behind it failed. */
export interface Reach {
  /** The union as one feature, or, when it failed, the footprints themselves. */
  outline: Area[];
  failed: boolean;
}

/**
 * The dashed outline framing the combined footprint: the footprints' union. Should a union throw,
 * the distinct footprints themselves, so each is still outlined once, flagged as failed. Repeated
 * footprints are dropped first (distinctAreas).
 *
 * @param {Area[]} areas - The drawn footprints.
 * @return {Reach} The outline, and whether the union failed.
 */
export function footprintUnion(areas: readonly Area[]): Reach {
  const distinct = distinctAreas(areas);
  if (distinct.length <= 1) return { outline: distinct, failed: false };
  try {
    let outline: Area | null = distinct[0];
    for (const area of distinct.slice(1)) {
      outline = union(outline, area) as Area | null;
      if (!outline) return { outline: distinct, failed: true };
    }
    return { outline: [outline], failed: false };
  } catch {
    return { outline: distinct, failed: true };
  }
}

/**
 * The red stack once the case is confirmed: each footprint with the common region cut out, so the
 * green sits on bare basemap and the red that remains is still shaded by how many footprints
 * overlap there. A footprint the region covers entirely leaves nothing. With no region, the
 * footprints as they are. `failed` when a cut throws: the map then draws no green rather than
 * green over a stack it couldn't clip.
 *
 * @param {Area[]} areas - The drawn footprints.
 * @param {Area | null} region - The common region, or null when there is none to cut out.
 * @return {object} `areas`, the stack to draw, and `failed`.
 */
export function stackOutside(
  areas: readonly Area[],
  region: Area | null
): { areas: Area[]; failed: boolean } {
  if (!region) return { areas: [...areas], failed: false };
  try {
    const cut = areas.flatMap((area) => {
      const rest = difference(area, region) as Area | null;
      return rest ? [{ ...rest, properties: area.properties }] : [];
    });
    return { areas: cut, failed: false };
  } catch {
    return { areas: [...areas], failed: true };
  }
}

/** The domains a run would refuse, and so the map's green waits on: all eight. */
export interface MapDomains {
  /** Unassigned domains, archived ones aside. */
  missing: ScenarioDomainKey[];
  archived: ScenarioDomainKey[];
}

/**
 * The missing and archived domains for a set of picks. Cheap, so it can follow every pick; the
 * map's geometry (mapGeometry) takes only whether any is left.
 *
 * @param {object} scenarios - The form's picks.
 * @param {ArchivedScenarioIds} archivedScenarioIds - The archived scenario per domain.
 * @return {MapDomains} The missing and archived domains.
 */
export function mapDomains(
  scenarios: Picks,
  archivedScenarioIds: ArchivedScenarioIds = {}
): MapDomains {
  const missing = SCENARIO_DOMAINS.filter(
    (domain) => !scenarios[domain.key]
  ).map((domain) => domain.key);
  return { missing, archived: archivedDomains(scenarios, archivedScenarioIds) };
}

/**
 * The map's state and common region from the footprints alone, given whether any domain is still
 * missing or archived. Takes no picks, so swapping one Price Deck or DCA scenario for another can't
 * rerun the intersection; only assigning or clearing one changes `blocked`.
 *
 * @param {ScenarioFootprint[]} footprints - The picked coverage scenarios' footprints.
 * @param {object} options - `blocked` while any domain is missing or archived, and `loading`
 *   while a changed pick's footprint has not arrived.
 * @return {object} The state and the region to draw green.
 */
export function mapGeometry(
  footprints: readonly ScenarioFootprint[],
  {
    blocked = false,
    loading = false,
  }: { blocked?: boolean; loading?: boolean } = {}
): { state: CaseMapState; region: Area | null } {
  const result = (state: CaseMapState, region: Area | null = null) => ({
    state,
    region,
  });
  const drawn = footprintFeatures(footprints).features;
  const overCap = footprints.some((f) => f.overCap);
  if (drawn.length === 0 && !overCap) return result('bare');
  // Too large to send says so whether or not the case is confirmed: there's no green either way.
  if (blocked) return result(overCap ? 'unavailable' : 'incomplete');
  if (loading) return result('pending');
  if (footprints.some((f) => !f.polygon && !f.overCap)) return result('empty');
  if (overCap) return result('unavailable');

  const { region, failed } = commonRegion(drawn);
  if (failed) return result('unavailable');
  return region ? result('complete', region) : result('empty');
}

/** Decimal places the common region is snapped to before the cut: about 10 cm. */
export const CUT_PRECISION = 6;

type Rings = GeoJSON.Position[][];

const snapRing = (
  ring: GeoJSON.Position[],
  factor: number
): GeoJSON.Position[] =>
  ring
    .map(([lng, lat]) => [
      Math.round(lng * factor) / factor,
      Math.round(lat * factor) / factor,
    ])
    .filter(
      (position, i, all) =>
        i === 0 ||
        position[0] !== all[i - 1][0] ||
        position[1] !== all[i - 1][1]
    );

/**
 * Twice a closed ring's signed area (the shoelace sum), in whole grid units: zero for a ring whose
 * corners lie on one line. Counted on the grid, not on the snapped floats, because a snapped
 * coordinate isn't exact in binary: a ring on a slanted line would leave a remainder near 1e-13
 * and read as enclosing area. Measured from the ring's first corner. At nine decimals a product can
 * pass the integers a double holds exactly (rings wider than about 0.1°), but a ring on one line
 * still sums to exactly zero: each term's two products are equal there, so they round alike and
 * cancel.
 *
 * @param {GeoJSON.Position[]} ring - A closed ring.
 * @param {number} factor - 10 to the decimals kept.
 * @return {number} Twice the signed area, in grid units.
 */
const doubledGridArea = (ring: GeoJSON.Position[], factor: number): number => {
  const grid = ring.map(([x, y]) => [
    Math.round(x * factor),
    Math.round(y * factor),
  ]);
  const [x0, y0] = grid[0];
  return grid
    .slice(1)
    .reduce(
      (sum, [x, y], i) =>
        sum + (grid[i][0] - x0) * (y - y0) - (x - x0) * (grid[i][1] - y0),
      0
    );
};

/**
 * Whether a snapped ring still encloses area: three distinct corners plus its closing position,
 * not all on one line. One that snapping folds into a point or a line is a sliver with no area.
 *
 * @param {number} factor - 10 to the decimals kept.
 * @return {Function} Whether a snapped ring encloses area.
 */
const enclosesArea =
  (factor: number) =>
  (ring: GeoJSON.Position[]): boolean =>
    ring.length >= 4 && doubledGridArea(ring, factor) !== 0;

/**
 * A polygon's rings snapped, less those that collapse (enclosesArea). A polygon whose outer ring
 * collapses is dropped with its holes.
 *
 * @param {Rings} rings - The polygon's outer ring, then its holes.
 * @param {number} factor - 10 to the decimals kept.
 * @return {Rings | null} The snapped rings, or null when the outer ring collapses.
 */
const snapPolygon = (rings: Rings, factor: number): Rings | null => {
  const [outer, ...holes] = rings.map((ring) => snapRing(ring, factor));
  const encloses = enclosesArea(factor);
  if (!encloses(outer)) return null;
  return [outer, ...holes.filter(encloses)];
};

/**
 * The area with its coordinates rounded to `decimals`. The common region's vertices are new
 * intersection points at full float precision, a hair off the footprints' own; cut from a
 * footprint as they are, the slivers between them nest so deep that polygon-clipping overflows the
 * stack (from about 5,000 vertices a footprint). Snapped to a coarser grid (CUT_PRECISION by
 * default), the cut succeeds up to the payload cap. commonRegion also snaps each clip's result to
 * the footprints' own 9-decimal grid (FOOTPRINT_DECIMALS), so a near copy clips cleanly. A ring
 * that snapping collapses (a sliver narrower than the grid) is dropped, so neither the next clip
 * nor the cut gets a degenerate ring.
 *
 * @param {Area} area - A polygon or multipolygon feature.
 * @param {number} decimals - Decimal places to keep.
 * @return {Area | null} The same feature, snapped; null when every part of it collapses.
 */
export function snapArea(
  area: Area,
  decimals: number = CUT_PRECISION
): Area | null {
  const factor = 10 ** decimals;
  const parts =
    area.geometry.type === 'Polygon'
      ? [area.geometry.coordinates]
      : area.geometry.coordinates;
  const polygons = parts
    .map((rings) => snapPolygon(rings, factor))
    .filter((rings): rings is Rings => rings !== null);
  if (polygons.length === 0) return null;
  const geometry: GeoJSON.Polygon | GeoJSON.MultiPolygon =
    area.geometry.type === 'Polygon'
      ? { type: 'Polygon', coordinates: polygons[0] }
      : { type: 'MultiPolygon', coordinates: polygons };
  return { ...area, geometry };
}

/** What the map draws, worked out from the footprints: the turf work, in one place. */
export interface CoverageResult {
  state: CaseMapState;
  /** The green: the common region, only once confirmed and cut out of the stack. */
  green: Area | null;
  /** The dashed frame: the footprints' union, or each footprint when the union failed. */
  outline: Area[];
  /** The red stack: each footprint, with the green cut out once confirmed. */
  stack: Area[];
  /** A union or a cut failed: the legend says coverage couldn't be computed for every area. */
  failed: boolean;
}

/**
 * Everything the map draws from the footprints: the state and common region (mapGeometry), the
 * dashed frame (footprintUnion) and the red stack with the green cut out (stackOutside). Pure and
 * serialisable either way, so it runs in the coverage worker (coverage-geometry.worker.ts) or, with
 * no Worker, on the main thread. The common region is snapped (snapArea)
 * before it is cut out, which keeps the cut clear of polygon-clipping's stack overflow at State
 * scale. Never throws: a clip that still fails in a way the steps don't catch themselves gives the
 * uncut stack, each footprint outlined, no green, and `failed`.
 *
 * @param {ScenarioFootprint[]} footprints - The picked coverage scenarios' footprints.
 * @param {object} options - `blocked` and `loading`, as mapGeometry takes them.
 * @return {CoverageResult} What to draw.
 */
export function computeCoverage(
  footprints: readonly ScenarioFootprint[],
  options: { blocked?: boolean; loading?: boolean } = {}
): CoverageResult {
  const features = footprintFeatures(footprints).features;
  try {
    const { state: exactState, region: exact } = mapGeometry(
      footprints,
      options
    );
    // Cut and drawn snapped, so the green fills exactly the hole cut for it (snapArea). A region
    // that snapping collapses entirely is no common region.
    const region = exact && snapArea(exact);
    const state = exactState === 'complete' && !region ? 'empty' : exactState;
    const reach = footprintUnion(features);
    const stack = stackOutside(features, state === 'complete' ? region : null);
    return {
      state,
      green: state === 'complete' && !stack.failed ? region : null,
      outline: reach.outline,
      stack: stack.areas,
      failed: stack.failed || reach.failed,
    };
  } catch {
    return {
      state:
        features.length === 0
          ? 'bare'
          : options.blocked
            ? 'incomplete'
            : 'unavailable',
      green: null,
      outline: [...features],
      stack: [...features],
      failed: true,
    };
  }
}
